import {useCallback, useEffect, useRef} from 'react';

import {
  PDP_PRICE_BREAKDOWN_SCROLL_OPTIONS,
  scrollElementBelowStickyHeader,
} from '@amblique/king-living/app/utils/utils';

/**
 * PDP scroll management for the Complex Product / Customize Your Own flow.
 *
 * ## Responsibilities
 *
 * All selection and gallery preview state has been consolidated into `CustomizeJourneyContext`:
 * - `committedSelection` holds the persisted source of truth for selections.
 * - `flyoutDraftState.galleryPreview` holds the temporary gallery preview state; it is
 *   automatically cleared by `cancelFlyout` (discard) and `commitFlyout` (done).
 *
 * This hook manages the post-commit scroll behaviour:
 * - After the user clicks **Done** in the customize flyout, scroll the page to the
 *   correct section based on the committed mode (passed via handleConfigClose):
 *   - `'set'` → `#choose-your-sets-anchor` (Choose from popular sets section)
 *   - `'customize'` → `#customize-your-own-anchor` (Customize your own section)
 *
 * @param {Object} options
 * @param {'choose-layout'|'customize'} options.configPanelMode - Current configuration panel mode.
 * @param {boolean} options.isConfigOpen - Whether the configuration panel/drawer is open.
 * @returns {{ handleConfigClose: Function, pendingScrollToCustomizeAnchorRef: React.MutableRefObject }}
 */
export function usePdpCustomizeMirrors({configPanelMode, isConfigOpen}) {
  /** Stores the anchor ID to scroll to after the panel closes, or false when no scroll is pending. */
  const pendingScrollToCustomizeAnchorRef = useRef(false);

  /**
   * Close path from ConfigurationSelector / panel.
   * @param {boolean} [discardFlyoutDraft=false] - True when the user dismissed without committing.
   * @param {'set'|'customize'|null} [committedMode=null] - The committed selection mode after Done.
   */
  const handleConfigClose = useCallback((discardFlyoutDraft = false, committedMode = null) => {
    if (configPanelMode === 'customize' && !discardFlyoutDraft) {
      pendingScrollToCustomizeAnchorRef.current = committedMode === 'set'
        ? 'choose-your-sets-anchor'
        : 'customize-your-own-anchor';
    }
  }, [configPanelMode]);

  // After config panel closes from customize Done: scroll to the determined anchor.
  // Clear `pending` only inside the inner rAF so Strict Mode remount does not drop the scroll.
  useEffect(() => {
    if (isConfigOpen) {
      return undefined;
    }
    if (!pendingScrollToCustomizeAnchorRef.current) {
      return undefined;
    }

    const anchorId = pendingScrollToCustomizeAnchorRef.current;
    let innerRaf = 0;
    let settleRaf = 0;

    const outerRaf = window.requestAnimationFrame(() => {
      innerRaf = window.requestAnimationFrame(() => {
        // Extra frame so ChooseYourSets layout (expand/set summary) has committed before measuring.
        settleRaf = window.requestAnimationFrame(() => {
          pendingScrollToCustomizeAnchorRef.current = false;
          scrollElementBelowStickyHeader(`#${anchorId}`, undefined, {
            scrollBehavior: 'auto',
            ...PDP_PRICE_BREAKDOWN_SCROLL_OPTIONS,
          });
        });
      });
    });

    return () => {
      window.cancelAnimationFrame(outerRaf);
      window.cancelAnimationFrame(innerRaf);
      window.cancelAnimationFrame(settleRaf);
    };
  }, [isConfigOpen]);

  return {
    handleConfigClose,
    pendingScrollToCustomizeAnchorRef,
  };
}
